/**
 * Isometric line drawings for the AI visibility process (Mätning, Källor och
 * fakta, Åtgärder, Bevakning). Same projection, frame, stroke and animation
 * hooks as the SEO and webbyrå drawings, with their own motifs and motion:
 * - 01 rays go out from the company's plinth to four pillars (the AI
 *   services), whose tops light up in turn
 * - 02 a shelf of scrolls and books; the sources that count fill in one by one
 * - 03 three stelae (facts set in stone) get their lines carved in turn
 * - 04 a column chart rises bar by bar, the newest bar pulses
 */

import type { CSSProperties } from "react";

import {
  box,
  CORNER_CROSSES,
  floorGrid,
  IllustrationFrame,
  line,
  polyline,
  project,
  type Point3,
  type ProcessIllustration,
  type Shape,
} from "@/components/services/seo/seo-process-illustrations";

/** A flat rectangle on a vertical face at depth y (spans x and z). */
function faceRect(x: number, y: number, z: number, w: number, h: number) {
  return polyline(
    [
      [x, y, z],
      [x + w, y, z],
      [x + w, y, z + h],
      [x, y, z + h],
    ],
    true,
  );
}

function dotAt(point: Point3) {
  const [cx, cy] = project(point);
  return { cx, cy };
}

// ---------------------------------------------------------------- 01 Mätning

const CENTER_TOP: Point3 = [50, 50, 16];
const PILLARS: [number, number][] = [
  [12, 12],
  [76, 12],
  [12, 76],
  [76, 76],
];
const PILLAR_SIZE = 12;
const PILLAR_HEIGHT = 26;

function MeasureIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(4, 4, 0, 92, 92, 4),
    // the company: a low plinth in the middle
    ...box(42, 42, 4, 16, 16, 12),
    ...PILLARS.flatMap(([x, y]) => box(x, y, 4, PILLAR_SIZE, PILLAR_SIZE, PILLAR_HEIGHT)),
  ];

  const construct = [
    ...floorGrid(100, 20).filter((_, index) => index % 2 === 0),
    polyline([[50, 4, 4], [50, 96, 4]]),
    polyline([[4, 50, 4], [96, 50, 4]]),
  ];

  const tops = PILLARS.map(
    ([x, y]) => [x + PILLAR_SIZE / 2, y + PILLAR_SIZE / 2, 4 + PILLAR_HEIGHT] as Point3,
  );

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <g className="cc-ill-ai-measure__rays">
        {tops.map((top, index) => (
          <path
            key={index}
            d={polyline([CENTER_TOP, top])}
            pathLength={1}
            style={{ "--cc-ill-segment": index } as CSSProperties}
          />
        ))}
      </g>
      {tops.map((top, index) => (
        <circle
          key={index}
          className="cc-ill-ai-measure__node"
          style={{ "--cc-ill-node": index } as CSSProperties}
          {...dotAt(top)}
          r={3}
        />
      ))}
      <circle className="cc-ill__dot" {...dotAt(CENTER_TOP)} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 02 Källor och fakta

const SHELF_Y = 40;
const SHELF_DEPTH = 14;
const SHELF_LEVELS = [8, 32];
/** Books per shelf: x, width, height. The `key` ones are the sources that count. */
const BOOKS: { x: number; w: number; h: number; level: number; key?: boolean }[] = [
  { x: 16, w: 6, h: 18, level: 0 },
  { x: 23, w: 7, h: 20, level: 0, key: true },
  { x: 31, w: 5, h: 16, level: 0 },
  { x: 40, w: 8, h: 19, level: 0 },
  { x: 52, w: 6, h: 17, level: 0, key: true },
  { x: 62, w: 7, h: 20, level: 0 },
  { x: 72, w: 6, h: 15, level: 0 },
  { x: 18, w: 7, h: 17, level: 1 },
  { x: 28, w: 6, h: 19, level: 1 },
  { x: 38, w: 7, h: 16, level: 1, key: true },
  { x: 50, w: 6, h: 18, level: 1 },
  { x: 60, w: 8, h: 20, level: 1 },
  { x: 72, w: 6, h: 17, level: 1, key: true },
];

function SourcesIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 30, 0, 100, 40, 4),
    // the shelf: two sides, two boards
    ...box(10, SHELF_Y, 4, 3, SHELF_DEPTH, 54),
    ...box(84, SHELF_Y, 4, 3, SHELF_DEPTH, 54),
    ...SHELF_LEVELS.flatMap((z) => box(13, SHELF_Y, 4 + z - 2, 71, SHELF_DEPTH, 2)),
    ...BOOKS.map((book) => ({
      d: faceRect(book.x, SHELF_Y, 4 + SHELF_LEVELS[book.level], book.w, book.h),
    })),
  ];

  const construct = [
    polyline([[10, SHELF_Y, 60], [90, SHELF_Y, 60]]),
    polyline([[10, SHELF_Y + 22, 4], [90, SHELF_Y + 22, 4]]),
  ];

  const keyBooks = BOOKS.filter((book) => book.key);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      {keyBooks.map((book, index) => (
        <path
          key={`${book.x}-${book.level}`}
          className="cc-ill-ai-sources__book"
          style={{ "--cc-ill-segment": index } as CSSProperties}
          d={faceRect(book.x, SHELF_Y, 4 + SHELF_LEVELS[book.level], book.w, book.h)}
        />
      ))}
      <circle className="cc-ill__dot" {...dotAt([75, SHELF_Y, 4 + SHELF_LEVELS[1] + 21])} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 03 Åtgärder

const STELAE_XS = [16, 43, 70];
const STELA_Y = 44;
const STELA_W = 14;
const STELA_H = 44;

function ActionsIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 26, 0, 100, 54, 4),
    ...STELAE_XS.flatMap((x) => [
      ...box(x - 2, STELA_Y - 2, 4, STELA_W + 4, 10, 3),
      ...box(x, STELA_Y, 7, STELA_W, 6, STELA_H),
    ]),
  ];

  const construct = [
    polyline([[8, STELA_Y, 7 + STELA_H + 6], [92, STELA_Y, 7 + STELA_H + 6]]),
    polyline([[8, STELA_Y, 7], [92, STELA_Y, 7]]),
  ];

  // Carved lines on each stela's front face, top to bottom.
  const carved = STELAE_XS.flatMap((x) =>
    [40, 33, 26, 19].map((z, row) =>
      line([x + 3, STELA_Y, 7 + z], [x + STELA_W - (row % 2 ? 5 : 3), STELA_Y, 7 + z]).d,
    ),
  );

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <g className="cc-ill-ai-actions__carve">
        {carved.map((d, index) => (
          <path key={index} d={d} pathLength={1} style={{ "--cc-ill-segment": index } as CSSProperties} />
        ))}
      </g>
      <circle className="cc-ill__dot" {...dotAt([STELAE_XS[2] + STELA_W / 2, STELA_Y, 7 + STELA_H])} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 04 Bevakning

const BAR_Y = 42;
const BARS: { x: number; h: number }[] = [
  { x: 16, h: 12 },
  { x: 34, h: 20 },
  { x: 52, h: 30 },
  { x: 70, h: 44 },
];

function MonitorIllustration({ maskId }: { maskId: string | null }) {
  const plate: Shape[] = [...box(0, 24, 0, 100, 56, 4)];

  const construct = [
    ...[12, 24, 36, 48].map((z) => polyline([[10, BAR_Y + 10, 4 + z], [92, BAR_Y + 10, 4 + z]])),
    polyline([[10, BAR_Y + 10, 4], [10, BAR_Y + 10, 54]]),
  ];

  const last = BARS[BARS.length - 1];
  const peak = project([last.x + 5, BAR_Y + 5, 4 + last.h]);

  return (
    <IllustrationFrame
      layers={[
        { shapes: plate },
        ...BARS.map((bar, index) => ({
          className: `cc-ill-ai-watch__bar cc-ill-ai-watch__bar--${index + 1}`,
          shapes: box(bar.x, BAR_Y, 4, 10, 10, bar.h),
        })),
      ]}
      construct={construct}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <path
        className="cc-ill-ai-watch__trend"
        d={polyline(BARS.map((bar) => [bar.x + 5, BAR_Y + 5, 4 + bar.h + 6] as Point3))}
        pathLength={1}
      />
      <circle className="cc-ill-ai-watch__pulse" cx={peak[0]} cy={peak[1]} r={3} />
      <circle className="cc-ill__dot" cx={peak[0]} cy={peak[1]} r={2.6} />
    </IllustrationFrame>
  );
}

export const AI_PROCESS_ILLUSTRATIONS: ProcessIllustration[] = [
  MeasureIllustration,
  SourcesIllustration,
  ActionsIllustration,
  MonitorIllustration,
];
